import React from "react";
import { Box, Tooltip } from "@mui/material";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import { dt } from "../../../shared/themes/redesignTokens";
import useTranslation from "../../../shared/hooks/useTranslation";

interface GroupByOption {
  value: string;
  labelKey: string;
}

const GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: "type", labelKey: "groupByType" },
  { value: "location", labelKey: "groupByLocation" },
  { value: "line", labelKey: "groupByLine" },
  { value: "department", labelKey: "groupByDepartment" },
  { value: "all", labelKey: "groupByAll" },
];

interface GroupTogglePillsProps {
  activeGroupBy: string;
  onGroupByChange: (groupBy: string) => void;
}

const GroupTogglePills: React.FC<GroupTogglePillsProps> = ({
  activeGroupBy,
  onGroupByChange,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        alignItems: "center",
      }}
    >
      {GROUP_BY_OPTIONS.map((opt) => {
        const isActive = activeGroupBy === opt.value;
        const isLocation = opt.value === "location";
        const pill = (
          <Box
            key={opt.value}
            onClick={() => onGroupByChange(opt.value)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: isLocation ? "5px" : 0,
              padding: "6px 16px",
              borderRadius: dt.radiusPill,
              fontSize: "13px",
              fontFamily: dt.font,
              fontWeight: isActive ? 600 : 400,
              letterSpacing: "0.1px",
              cursor: "pointer",
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              userSelect: "none",
              whiteSpace: "nowrap",
              backgroundColor: isActive ? dt.blue : "#F1F3F5",
              color: isActive ? "#FFFFFF" : dt.textSecondary,
              border: isActive
                ? `1px solid ${dt.blue}`
                : "1px solid transparent",
              boxShadow: isActive
                ? "0 2px 8px rgba(52,152,219,0.25)"
                : "none",
              "&:hover": {
                backgroundColor: isActive ? dt.blueHover : "#E8EAED",
                color: isActive ? "#FFFFFF" : dt.textPrimary,
                transform: "translateY(-1px)",
                boxShadow: isActive
                  ? "0 4px 12px rgba(52,152,219,0.3)"
                  : "0 1px 4px rgba(0,0,0,0.06)",
              },
            }}
          >
            {isLocation && <PlaceOutlined sx={{ fontSize: "15px" }} />}
            {translate(opt.labelKey)}
          </Box>
        );
        return isLocation ? (
          <Tooltip key={opt.value} title={translate("locationUsesAuthoritative")} arrow placement="top">
            {pill}
          </Tooltip>
        ) : pill;
      })}
    </Box>
  );
};

export default GroupTogglePills;
